import React, { useState, useEffect, useReducer } from "react";
import { useAuditApi } from "../../services/AuditApi";
import Heading from "carbon-react/lib/components/heading";
import AuditLogFilter, { searchByKeys } from "./AuditLogFilter";
import AuditLogTable from "./AuditLogTable";
import ErrorMessage from "../messages/ErrorMessage";
import {
  getEmptyPageMetadata,
  getPreviousPageTokenForPageRead,
  pagingReducer,
} from "../../utils/pageMetadataHelper";
import { auditOperations } from "../../constants/model";
import useQueryString from "../../hooks/useQueryString/useQueryString";
import { isValidISODateString } from "../../constants/validation";

import "./AuditLogs.css";

const qspParamNames = Object.freeze({
  searchBy: "filterBy",
  searchValue: "filterValue",
  fromDateUtc: "fromDateUtc",
  toDateUtc: "toDateUtc",
});

const headerRow = (
  <Heading className="audit-logs-header" title="Audit logs" divider={false} />
);

const buildInitialDateRange = () => {
  const now = new Date();
  const aMonthAgo = new Date();
  aMonthAgo.setMonth(aMonthAgo.getMonth() - 1);
  return {
    dateFromUtc: new Date(
      aMonthAgo.getFullYear(),
      aMonthAgo.getMonth(),
      aMonthAgo.getDate()
    ).toISOString(),
    dateToUtc: new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59
    ).toISOString(),
  };
};

const AuditLogs = () => {
  const pagingDefaults = {
    pageSize: 15,
  };
  const { fetchAuditLogs } = useAuditApi();
  const [fetchAuditLogsFailed, setFetchAuditLogsFailed] = useState(false);
  const [fetchErrorMsg, setFetchErrorMsg] = useState("");
  const [cachedAuditLogPageSize, setCachedAuditLogPageSize] = useState(
    pagingDefaults.pageSize
  );
  const [pageMetadata, dispatchPageMetadata] = useReducer(
    pagingReducer,
    getEmptyPageMetadata()
  );
  const [loading, setLoading] = useState(false);
  const [searchBy, setSearchBy] = useQueryString(
    qspParamNames.searchBy,
    searchByKeys.all
  );
  const [searchValue, setSearchValue] = useQueryString(
    qspParamNames.searchValue,
    ""
  );
  const initialDateRange = buildInitialDateRange();
  const [searchDateFromUtc, setSearchDateFromUtc] = useQueryString(
    qspParamNames.fromDateUtc,
    initialDateRange.dateFromUtc
  );
  const [searchDateToUtc, setSearchDateToUtc] = useQueryString(
    qspParamNames.toDateUtc,
    initialDateRange.dateToUtc
  );

  useEffect(() => {
    getAuditLogs();
  }, []); // eslint-disable-line

  const buildEmptyResultSet = () => {
    return {
      auditLogs: [],
      pageSize: cachedAuditLogPageSize,
    };
  };
  const [auditLogResultSet, setAuditLogResultSet] = useState(
    buildEmptyResultSet()
  );

  const isDateRangeValid = (dateFromUtc, dateToUtc) => {
    try {
      return dateFromUtc && dateToUtc && dateFromUtc < dateToUtc;
    } catch {} //Ignore
    return false;
  };

  const clearError = () => {
    setFetchAuditLogsFailed(false);
    setFetchErrorMsg("");
  };

  const showError = (msg) => {
    setFetchAuditLogsFailed(true);
    setFetchErrorMsg(msg);
  };

  const getAuditLogs = async (
    _searchBy = searchBy,
    _searchValue = searchValue,
    _dateFromUtc = searchDateFromUtc,
    _dateToUtc = searchDateToUtc,
    pageSize = cachedAuditLogPageSize,
    continuationToken = null
  ) => {
    try {
      setLoading(true);
      clearError();

      sanitizeSearchBy(_searchBy, (sanitized) => {
        _searchBy = sanitized;
        setSearchBy(sanitized);
      });

      sanitizeSearchValue(_searchBy, _searchValue, (sanitized) => {
        _searchValue = sanitized;
        setSearchValue(sanitized);
      });

      if (!isValidISODateString(_dateFromUtc)) {
        _dateFromUtc = initialDateRange.dateFromUtc;
        setSearchDateFromUtc(_dateFromUtc);
      }

      if (!isValidISODateString(_dateToUtc)) {
        _dateToUtc = initialDateRange.dateToUtc;
        setSearchDateToUtc(_dateToUtc);
      }

      const parsedFromUtc = new Date(_dateFromUtc);
      const parsedToUtc = new Date(_dateToUtc);

      const fetchAuditLogsViaApi = async () => {
        if (_searchBy === searchByKeys.all) _searchValue = null;

        const apiResult = await fetchAuditLogs(
          _searchBy,
          _searchValue,
          parsedFromUtc,
          parsedToUtc,
          pageSize,
          continuationToken
        );

        if (!apiResult?.success || !apiResult?.data)
          throw new Error(
            apiResult?.error?.description || "Unexpected response"
          );

        return {
          auditLogs: apiResult.data.auditLogs,
          pageSize,
          nextPageToken: apiResult.data.forwardToken,
        };
      };

      const resultSet = isDateRangeValid(parsedFromUtc, parsedToUtc)
        ? await fetchAuditLogsViaApi()
        : buildEmptyResultSet();

      dispatchPageMetadata({
        type: "ON_PAGE_READ_SUCCESS",
        currentPageToken: continuationToken,
        nextPageToken: resultSet.nextPageToken,
      });

      setAuditLogResultSet(resultSet);
    } catch (error) {
      showError(error.toString());
    }
    setLoading(false);
  };

  const sanitizeSearchBy = (_searchBy, onSanitized) => {
    for (const key in searchByKeys) {
      if (_searchBy === searchByKeys[key]) {
        return;
      }
    }
    onSanitized(searchByKeys.all);
  };

  const sanitizeSearchValue = (_searchBy, _searchValue, onSanitized) => {
    if (_searchBy === searchByKeys.all && _searchValue !== "") {
      onSanitized("");
      return;
    }

    if (_searchBy === searchByKeys.operation && _searchValue !== "") {
      var match = auditOperations.filter((operation) => {
        return operation.value === _searchValue;
      });
      if (!match || match.length < 1) {
        onSanitized(auditOperations[0].value);
        return;
      }
    }
  };

  const stringHasValue = (stringValue) => {
    return stringValue && stringValue.length > 0;
  };

  const onSearch = async (overrideSearchBy, overrideSearchValue) => {
    return !stringHasValue(overrideSearchBy) &&
      !stringHasValue(overrideSearchValue)
      ? await getAuditLogs()
      : await getAuditLogs(
          overrideSearchBy,
          overrideSearchValue,
          searchDateFromUtc,
          searchDateToUtc,
          cachedAuditLogPageSize
        );
  };

  const onPagePrev = async () => {
    const prevPageToken = getPreviousPageTokenForPageRead(
      pageMetadata,
      dispatchPageMetadata
    );

    await getAuditLogs(
      searchBy,
      searchValue,
      searchDateFromUtc,
      searchDateToUtc,
      cachedAuditLogPageSize,
      prevPageToken
    );
  };

  const onPageNext = async () => {
    if (!pageMetadata?.nextPageToken || pageMetadata.nextPageToken.length < 1) {
      return;
    }

    await getAuditLogs(
      searchBy,
      searchValue,
      searchDateFromUtc,
      searchDateToUtc,
      cachedAuditLogPageSize,
      pageMetadata.nextPageToken
    );
  };

  const onSearchDateRangeUpdate = async (newFromDateUtc, newToDateUtc) => {
    setSearchDateFromUtc(newFromDateUtc);
    setSearchDateToUtc(newToDateUtc);
    await getAuditLogs(searchBy, searchValue, newFromDateUtc, newToDateUtc);
  };

  return (
    <>
      {headerRow}
      <AuditLogFilter
        searchValue={searchValue}
        onSearchValueUpdate={setSearchValue}
        searchBy={searchBy}
        onSearchByUpdate={setSearchBy}
        searchDateFromUtc={searchDateFromUtc}
        searchDateToUtc={searchDateToUtc}
        onSearchDateRangeUpdate={onSearchDateRangeUpdate}
        onSearch={onSearch}
      />
      {fetchAuditLogsFailed ? (
        <ErrorMessage
          message={
            fetchErrorMsg && fetchErrorMsg.length > 0
              ? fetchErrorMsg
              : "Failed to read audit log data"
          }
        />
      ) : (
        <AuditLogTable
          auditLogResultSet={auditLogResultSet}
          loading={loading}
          onPagePrev={onPagePrev}
          onPageNext={onPageNext}
          pageNo={pageMetadata.previousPageTokens.length + 1}
          lastPage={!pageMetadata.nextPageToken}
        />
      )}
    </>
  );
};

export default AuditLogs;
